export const COLOR_SCHEMES = [
  {
    name: 'Ocean Blue',
    primary: '#2563EB',
    secondary: '#4F46E5',
    accent: '#9333EA',
    background: '#FFFFFF',
    text: '#1F2937',
    surface: '#F8FBFF',
  },
  {
    name: 'Forest Green',
    primary: '#10B981',
    secondary: '#0EA5E9',
    accent: '#F59E0B',
    background: '#F9FAFB',
    text: '#111827',
    surface: '#F3FBF8',
  },
  {
    name: 'Royal Purple',
    primary: '#6366F1',
    secondary: '#8B5CF6',
    accent: '#EC4899',
    background: '#FFFFFF',
    text: '#374151',
    surface: '#F7F7FF',
  },
  {
    name: 'Midnight Dark',
    primary: '#0F172A',
    secondary: '#334155',
    accent: '#F97316',
    background: '#F8FAFC',
    text: '#0F172A',
    surface: '#F4F7FB',
  },
  {
    name: 'Rose Gold',
    primary: '#BE185D',
    secondary: '#9D174D',
    accent: '#2563EB',
    background: '#FFF1F2',
    text: '#881337',
    surface: '#FFF7F8',
  },
];

export const DEFAULT_PROFILE = {
  template: 'business',
  colorSchemeIndex: 0,
  heroHeading: 'Welcome to {company}',
  heroSubheading: 'Professional services you can trust',
  ctaText: 'Contact Us',
  trustSignals: ['Fast launch', 'Custom by demand', 'ICP-ready handoff'],
  features: [
    {
      icon: '⭐',
      title: 'Quality Service',
      description: 'We shape a polished landing page around your business and ICP.',
    },
    {
      icon: '🤝',
      title: 'Customer First',
      description: 'Messaging is tuned to your niche, offer, and chosen location.',
    },
    {
      icon: '📞',
      title: 'Always Available',
      description: 'A lead capture form is included so outreach traffic has somewhere to convert.',
    },
  ],
};

export const INDUSTRY_PROFILES = {
  dental: {
    template: 'service',
    colorSchemeIndex: 1,
    heroHeading: 'Your Smile Deserves the Best Care',
    heroSubheading: 'Professional dental services for the whole family',
    ctaText: 'Book Your Appointment',
    trustSignals: ['5-star patient care', 'Modern equipment', 'Easy booking'],
    features: [
      { icon: '🦷', title: 'General Dentistry', description: 'Checkups, cleanings, and preventive care for every age.' },
      { icon: '✨', title: 'Cosmetic Dentistry', description: 'Whitening, veneers, and smile makeovers that lift confidence.' },
      { icon: '🔧', title: 'Restorative Care', description: 'Crowns, bridges, implants, and root canals with modern workflows.' },
    ],
  },
  plumbing: {
    template: 'service',
    colorSchemeIndex: 0,
    heroHeading: 'Professional Plumbing You Can Trust',
    heroSubheading: 'Fast, reliable service with emergency response when it matters',
    ctaText: 'Get a Free Quote',
    trustSignals: ['24/7 response', 'Licensed team', 'Upfront pricing'],
    features: [
      { icon: '🔧', title: 'Emergency Repairs', description: 'Burst pipes, leaks, and urgent fixes handled without delay.' },
      { icon: '🚿', title: 'Installation', description: 'Fixtures, pipes, and upgrades installed cleanly and correctly.' },
      { icon: '🔍', title: 'Inspections', description: 'Detailed leak detection and system checks before bigger issues appear.' },
    ],
  },
  hvac: {
    template: 'service',
    colorSchemeIndex: 0,
    heroHeading: 'Keep Your Home Comfortable Year-Round',
    heroSubheading: 'Heating, cooling, and air quality handled by trusted local specialists',
    ctaText: 'Schedule Service',
    trustSignals: ['Same-day service', 'Certified techs', 'Seasonal tune-ups'],
    features: [
      { icon: '❄️', title: 'AC Repair & Install', description: 'Efficient cooling systems sized and installed for your space.' },
      { icon: '🔥', title: 'Heating Systems', description: 'Furnace and heat pump service that keeps winters predictable.' },
      { icon: '🌬️', title: 'Air Quality', description: 'Filtration, duct, and purification solutions for cleaner indoor air.' },
    ],
  },
  restaurant: {
    template: 'business',
    colorSchemeIndex: 2,
    heroHeading: 'A Dining Experience Guests Remember',
    heroSubheading: 'Fresh ingredients, standout presentation, and warm hospitality',
    ctaText: 'View Menu & Reserve',
    trustSignals: ['Online reservations', 'Fresh ingredients', 'Private events'],
    features: [
      { icon: '🍽️', title: 'Signature Menu', description: 'Plates designed to turn first-time visitors into repeat guests.' },
      { icon: '🥂', title: 'Private Events', description: 'Celebrations, team dinners, and occasions hosted with care.' },
      { icon: '🚗', title: 'Catering & Delivery', description: 'Serve customers in-house, at home, or at the next event.' },
    ],
  },
  legal: {
    template: 'service',
    colorSchemeIndex: 3,
    heroHeading: 'Experienced Legal Representation',
    heroSubheading: 'Clear advice and confident advocacy when the stakes are high',
    ctaText: 'Free Consultation',
    trustSignals: ['Confidential intake', 'Transparent process', 'Responsive counsel'],
    features: [
      { icon: '⚖️', title: 'Personal Injury', description: 'Guidance for cases that need strong representation and steady communication.' },
      { icon: '📋', title: 'Business Law', description: 'Contracts, disputes, and commercial matters handled with precision.' },
      { icon: '🏠', title: 'Real Estate Law', description: 'Transactions, closings, and property disputes kept on track.' },
    ],
  },
  fitness: {
    template: 'business',
    colorSchemeIndex: 1,
    heroHeading: 'Transform Your Body, Transform Your Life',
    heroSubheading: 'Coaching, classes, and accountability that make momentum stick',
    ctaText: 'Start Your Free Trial',
    trustSignals: ['Certified trainers', 'Flexible plans', 'Community support'],
    features: [
      { icon: '💪', title: 'Personal Training', description: 'One-on-one programs aligned to goals, injuries, and timelines.' },
      { icon: '🧘', title: 'Group Classes', description: 'Strength, conditioning, yoga, and cardio sessions for every level.' },
      { icon: '📊', title: 'Nutrition Plans', description: 'Sustainable meal guidance to support performance and recovery.' },
    ],
  },
  real_estate: {
    template: 'business',
    colorSchemeIndex: 3,
    heroHeading: 'Find Your Dream Home With Expert Guidance',
    heroSubheading: 'Buying, selling, and investing backed by local market knowledge',
    ctaText: 'Browse Listings',
    trustSignals: ['Local expertise', 'Fast follow-up', 'Trusted advisors'],
    features: [
      { icon: '🏠', title: 'Buying', description: 'Find homes that fit budget, timing, and long-term goals.' },
      { icon: '💰', title: 'Selling', description: 'Position properties with better presentation and stronger demand.' },
      { icon: '📈', title: 'Investment', description: 'Spot high-potential opportunities with clearer upside.' },
    ],
  },
  auto_repair: {
    template: 'service',
    colorSchemeIndex: 0,
    heroHeading: 'Expert Auto Repair & Maintenance',
    heroSubheading: 'Honest diagnostics, fair pricing, and dependable turnaround',
    ctaText: 'Book Appointment',
    trustSignals: ['Certified mechanics', 'Fast diagnostics', 'Fair estimates'],
    features: [
      { icon: '🔧', title: 'General Repairs', description: 'Brakes, engine, and transmission work completed with care.' },
      { icon: '🛢️', title: 'Maintenance', description: 'Oil changes, tune-ups, and scheduled service that prevent bigger repairs.' },
      { icon: '🔍', title: 'Diagnostics', description: 'Modern diagnostics that identify issues quickly and accurately.' },
    ],
  },
  salon: {
    template: 'business',
    colorSchemeIndex: 4,
    heroHeading: 'Look Your Best, Feel Your Best',
    heroSubheading: 'Premium beauty services with a polished experience from first click',
    ctaText: 'Book Now',
    trustSignals: ['Online booking', 'Beauty specialists', 'Premium experience'],
    features: [
      { icon: '💇', title: 'Hair Styling', description: 'Cuts, color, and styling tailored to the client and occasion.' },
      { icon: '💅', title: 'Nail Services', description: 'Manicures, pedicures, and detailed nail art in a refined setting.' },
      { icon: '✨', title: 'Spa Treatments', description: 'Facials and care packages that feel premium from intake to finish.' },
    ],
  },
  consulting: {
    template: 'service',
    colorSchemeIndex: 3,
    heroHeading: 'Strategic Consulting That Drives Results',
    heroSubheading: 'Expert guidance to grow your business from strategy to execution',
    ctaText: 'Book a Strategy Call',
    trustSignals: ['Board-level insight', 'Practical execution', 'ROI focused'],
    features: [
      { icon: '💼', title: 'Business Strategy', description: 'Sharper positioning, pricing, and go-to-market decisions.' },
      { icon: '📊', title: 'Operational Excellence', description: 'Streamline operations and reduce friction across teams.' },
      { icon: '🚀', title: 'Growth & Scaling', description: 'Frameworks that turn growth plans into measurable action.' },
    ],
  },
  technology: {
    template: 'business',
    colorSchemeIndex: 3,
    heroHeading: 'Ship the AI Product Buyers Actually Test',
    heroSubheading: 'Positioning, proof, and a crisp story for design partners and early revenue',
    ctaText: 'Join the beta',
    trustSignals: ['Model-agnostic messaging', 'Security-first narrative', 'API-ready positioning'],
    features: [
      { icon: '◆', title: 'Launch narrative', description: 'Hero and proof blocks tuned for technical buyers and investors.' },
      { icon: '◆', title: 'Trust and safety', description: 'Clear language on data, evaluation, and deployment paths.' },
      { icon: '◆', title: 'Integrations story', description: 'SDKs, webhooks, and ecosystem hooks surfaced above the fold.' },
    ],
  },
  ecommerce: {
    template: 'business',
    colorSchemeIndex: 2,
    heroHeading: 'Shop the Best Deals Online',
    heroSubheading: 'Quality products, fast shipping, and an easy purchase experience',
    ctaText: 'Shop Now',
    trustSignals: ['Fast shipping', 'Easy returns', 'Secure checkout'],
    features: [
      { icon: '🛍️', title: 'Curated Selection', description: 'Product collections positioned for clarity and conversion.' },
      { icon: '🚚', title: 'Fast Shipping', description: 'Delivery expectations communicated up front and clearly.' },
      { icon: '🔄', title: 'Easy Returns', description: 'Policies built to reduce hesitation and increase confidence.' },
    ],
  },
  medical: {
    template: 'service',
    colorSchemeIndex: 1,
    heroHeading: 'Compassionate Healthcare You Can Trust',
    heroSubheading: 'Primary care and ongoing support designed around patient confidence',
    ctaText: 'Book an Appointment',
    trustSignals: ['Accepting patients', 'Trusted clinicians', 'Easy intake'],
    features: [
      { icon: '🩺', title: 'Primary Care', description: 'Checkups, screenings, and ongoing health support under one roof.' },
      { icon: '💉', title: 'Preventive Medicine', description: 'Early detection and wellness services for long-term health.' },
      { icon: '🏥', title: 'Specialized Treatment', description: 'Referrals and coordination for more advanced care needs.' },
    ],
  },
  construction: {
    template: 'service',
    colorSchemeIndex: 0,
    heroHeading: 'Quality Construction You Can Count On',
    heroSubheading: 'Licensed, insured, and built to last across residential and commercial jobs',
    ctaText: 'Get a Free Estimate',
    trustSignals: ['Licensed & insured', 'On-budget delivery', 'Clear estimates'],
    features: [
      { icon: '🏗️', title: 'Residential Projects', description: 'New builds, additions, and projects that improve daily living.' },
      { icon: '🏢', title: 'Commercial Work', description: 'Retail, office, and mixed-use construction with reliable oversight.' },
      { icon: '🔨', title: 'Renovations', description: 'Remodeling work that updates function without sacrificing finish.' },
    ],
  },
};

const KEYWORD_MAP = {
  dent: 'dental',
  tooth: 'dental',
  orthodont: 'dental',
  plumb: 'plumbing',
  pipe: 'plumbing',
  hvac: 'hvac',
  air_condition: 'hvac',
  heating: 'hvac',
  restaurant: 'restaurant',
  food: 'restaurant',
  beverage: 'restaurant',
  cafe: 'restaurant',
  bar: 'restaurant',
  law: 'legal',
  attorney: 'legal',
  lawyer: 'legal',
  legal: 'legal',
  gym: 'fitness',
  fitness: 'fitness',
  personal_train: 'fitness',
  workout: 'fitness',
  real_estate: 'real_estate',
  realtor: 'real_estate',
  property: 'real_estate',
  auto: 'auto_repair',
  mechanic: 'auto_repair',
  car_repair: 'auto_repair',
  salon: 'salon',
  beauty: 'salon',
  hair: 'salon',
  spa: 'salon',
  nail: 'salon',
  consult: 'consulting',
  advisory: 'consulting',
  management: 'consulting',
  tech: 'technology',
  saas: 'technology',
  software: 'technology',
  app: 'technology',
  ai: 'technology',
  ml: 'technology',
  dev: 'technology',
  fintech: 'technology',
  startup: 'technology',
  e_commerce: 'ecommerce',
  ecommerce: 'ecommerce',
  retail: 'ecommerce',
  shop: 'ecommerce',
  store: 'ecommerce',
  medical: 'medical',
  healthcare: 'medical',
  clinic: 'medical',
  doctor: 'medical',
  physician: 'medical',
  hospital: 'medical',
  construct: 'construction',
  contractor: 'construction',
  builder: 'construction',
  roofing: 'construction',
  remodel: 'construction',
};

export const normalizeIndustry = (rawIndustry = '') => (rawIndustry || '').toLowerCase().trim().replace(/\s+/g, '_').replace(/-/g, '_');

export const resolveIndustryKey = (rawIndustry) => {
  const normalized = normalizeIndustry(rawIndustry);
  if (!normalized) {
    return 'default';
  }
  if (INDUSTRY_PROFILES[normalized]) {
    return normalized;
  }
  return Object.entries(KEYWORD_MAP).find(([keyword]) => normalized.includes(keyword))?.[1] || 'default';
};

export const getIndustryProfile = (rawIndustry) => {
  const key = resolveIndustryKey(rawIndustry);
  return {
    key,
    isSupported: key !== 'default',
    profile: INDUSTRY_PROFILES[key] || DEFAULT_PROFILE,
  };
};

export const getColorScheme = (rawIndustry) => {
  const { profile } = getIndustryProfile(rawIndustry);
  return COLOR_SCHEMES[profile.colorSchemeIndex] || COLOR_SCHEMES[0];
};
